import classNames from 'classnames';
import { Link as ScrollLink } from 'react-scroll';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';

// context
import { useLayout, useLayoutUpdate } from '../../../../context/LayoutProvider/LayoutProvider';
import { useLanguage } from '../../../../context/LanguageProvider/LanguageProvider';
import { useAuth } from '../../../../context/AuthProvider/AuthProvider';
import { EXPERTISE_ITEMS } from '../../../../data/expertises';

// components
import NioIcon from '../../../NioIcon/NioIcon';
import NioButton from '../../../NioButton/NioButton';

const SUBMENU_LIST_SELECTOR = '.nk-nav-accueil-list, .nk-nav-expertises-list';
const HOME_SCROLL_PATHS = ['/'];

function isHomeScrollPath(pathname) {
  return HOME_SCROLL_PATHS.includes(pathname);
}

function parseHashUrl(url) {
  const hashIdx = url.indexOf('#');
  if (hashIdx === -1) return null;
  return {
    path: url.slice(0, hashIdx) || null,
    hash: url.slice(hashIdx + 1),
  };
}

function NavHashLink({ url, className, children, onClick }) {
  const location = useLocation();
  const parsed = parseHashUrl(url);

  if (parsed && isHomeScrollPath(location.pathname)) {
    const pathOk =
      !parsed.path ||
      parsed.path === location.pathname;
    if (pathOk) {
      return (
        <ScrollLink
          offset={-72}
          to={parsed.hash}
          className={className}
          onClick={onClick}
        >
          {children}
        </ScrollLink>
      );
    }
  }

  return (
    <Link to={url} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

export default function HeaderMenu({ data, className, handler, buttonContent }) {

  const layout = useLayout();
  const layoutUpdate = useLayoutUpdate();
  const navigate = useNavigate();
  const { tNav, t } = useLanguage();
  const { isAuthenticated, isAdmin, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const compClasses = classNames({
    [`${className}`]: className,
  });

  return (
    <>

      {
        layout.headerActive &&
        <div className="navbar-overlay" onClick={layoutUpdate.headerMobile}></div>
      }

      <nav className={compClasses}>
        <div>
          <HeaderMenu.Nav data={data} handler={handler} tNav={tNav} />
          <div className="nk-navbar-btn d-lg-none">
            <ul className="nk-btn-group sm justify-content-center flex-column gap-2">
              {isAuthenticated ? (
                <>
                  {isAdmin ? (
                    <li className="w-100">
                      <NioButton
                        as="link"
                        to="/admin"
                        icon="dashboard"
                        label={t('nav.adminDashboard')}
                        className={`${buttonContent.class} w-100`}
                      />
                    </li>
                  ) : null}
                  <li className="w-100">
                    <NioButton
                      icon="signout"
                      label={t('nav.logout')}
                      className={`${buttonContent.class} w-100 btn-outline-secondary`}
                      onClick={handleLogout}
                    />
                  </li>
                </>
              ) : (
                <li className="w-100">
                  <NioButton
                    as="link"
                    icon={buttonContent.icon || 'bag'}
                    to={buttonContent.redirect || '/auth/login'}
                    label={tNav(buttonContent.label || 'Login')}
                    className={buttonContent.class + ' w-100'}
                  />
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>
    </>
  )
}

function isExpertisesMenu(menu) {
  const type = menu.item.submenuType;
  return type === 'expertises-list' || type === 'domains-list';
}

function isAccueilListMenu(menu) {
  return menu.item.submenuType === 'accueil-list';
}

function handleSubmenuNavClick(e, layout, layoutUpdate) {
  e.stopPropagation();
  if (layout.headerActive) {
    layoutUpdate.headerMobile();
  }
}

function NioNav({ data, className, handler, tNav }) {
  const location = useLocation();
  const { t } = useLanguage();
  const layout = useLayout();
  const layoutUpdate = useLayoutUpdate();

  const compClasses = classNames({
    "nk-nav": true,
    [`${className}`]: className
  });

  const navItemClasses = classNames({
    "nk-nav-item": true,
  });

  const navLinkClasses = classNames({
    "nk-nav-link": true,
  });

  const menuHasSub = (menu) => {
    if (isExpertisesMenu(menu)) {
      return EXPERTISE_ITEMS.length > 0;
    }
    if (isAccueilListMenu(menu)) {
      return menu.item.submenu?.items?.length > 0;
    }
    return menu.item.submenu.items.length !== 0;
  };

  return (
    <ul className={compClasses}>
      {data && data.map((menu, idx) => {
        const hasSub = menuHasSub(menu);
        const prominent =
          menu.item.url === '/' || menu.item.url === '/portfolio' || menu.item.url === '/realisations';
        const linkExtra = `${hasSub ? 'nk-nav-toggle' : ''}${prominent ? ' nk-nav-link-prominent' : ''}`;
        const isExpertisesList = isExpertisesMenu(menu);
        const isAccueilList = isAccueilListMenu(menu);
        const parentUrl = menu.item.url;

        return (
        <li
          key={idx}
          onClick={(e) => {
            if (e.target.closest(SUBMENU_LIST_SELECTOR)) return;
            handler.menuToggle(e);
          }}
          className={
            navItemClasses +
            ` ${hasSub ? 'has-sub' : ''}` +
            `${isExpertisesList ? ' has-sub-expertises' : ''}` +
            `${isAccueilList ? ' has-sub-accueil' : ''}`
          }
        >
          {parseHashUrl(parentUrl) && isHomeScrollPath(location.pathname) ? (
            <NavHashLink
              url={parentUrl}
              className={`${navLinkClasses} ${linkExtra}`.trim()}
            >
              <span className="nk-nav-text">{tNav(menu.item.title)}</span>
            </NavHashLink>
          ) : location.pathname === "/" && parentUrl.startsWith('#') ? (
            <ScrollLink
              offset={-72}
              to={parentUrl.slice(1)}
              className={`${navLinkClasses} ${linkExtra}`.trim()}
            >
              <span className="nk-nav-text">{tNav(menu.item.title)}</span>
            </ScrollLink>
          ) : (
            <Link
              to={parentUrl}
              className={`${navLinkClasses} ${linkExtra}`.trim()}
            >
              <span className="nk-nav-text">{tNav(menu.item.title)}</span>
            </Link>
          )}

          {isExpertisesList ? (
            <HeaderMenu.ExpertisesListMenu
              viewAllUrl={menu.item.submenu.viewAllUrl || '/expertises'}
              layout={layout}
              layoutUpdate={layoutUpdate}
              t={t}
            />
          ) : isAccueilList ? (
            <HeaderMenu.AccueilListMenu
              items={menu.item.submenu.items}
              layout={layout}
              layoutUpdate={layoutUpdate}
              tNav={tNav}
            />
          ) : (
            <HeaderMenu.DropMenu data={menu.item.submenu} className={menu.item.mega && "nk-nav-mega-lg-home"} />
          )}
        </li>
        );
      })}
    </ul>
  );
}

function NioDropMenu({ className, data }) {

  const { media, items } = data;
  const { tNav } = useLanguage();
  const compClases = classNames({
    "nk-nav-sub nk-nav-mega nk-nav-mega-lg": true,
    [`${className}`]: className,
  });

  return (
    <>
      {
        items.length !== 0 &&
        <ul className={compClases}>
          <li className="nk-nav-item col-lg-8">
            <ul className='row mx-auto'>
              {
                items.map((item, idx) => (
                  <li className="col-lg-4 p-0" key={idx}>
                    {
                      !item.color ?
                        <>
                          {parseHashUrl(item.url) ? (
                            <NavHashLink url={item.url} className="nk-nav-link">
                              {tNav(item.title)}
                            </NavHashLink>
                          ) : (
                            <NavLink to={item.url} className="nk-nav-link">
                              {tNav(item.title)}
                            </NavLink>
                          )}
                        </>
                        :
                        <>
                          <NavLink to={item.url} className="nk-nav-link">

                            <div className="media-group">
                              <div className={`text-${item.color} me-3`}>
                                <NioIcon name={item.icon} />
                              </div>

                              <div className="media-text sm">
                                <span className="lead-text">{tNav(item.title)}</span>
                                <span className="sub-text m-0">{item.subtitle ? tNav(item.subtitle) : null}</span>
                              </div>
                            </div>
                          </NavLink>
                        </>
                    }
                  </li>
                ))
              }
            </ul>
          </li>
          {
            media &&
            <li className="nk-nav-item col-lg-4 nk-nav-media d-none d-lg-block">
              <Link to="https://themeforest.net/user/softnio/portfolio" traget="_blank">
                <img src="/images/promo-banner/promo-1.png" alt="promo-banner" className="rounded-3" />
              </Link>
            </li>
          }
        </ul>
      }
    </>
  )
}

function NioExpertisesListMenu({ viewAllUrl, layout, layoutUpdate, t }) {
  const compClasses = classNames({
    'nk-nav-sub nk-nav-expertises-list': true,
  });

  if (!EXPERTISE_ITEMS.length) return null;

  const onNavClick = (e) => handleSubmenuNavClick(e, layout, layoutUpdate);

  return (
    <ul className={compClasses}>
      <li className="nk-nav-expertises-viewall">
        <Link
          to={viewAllUrl}
          className="nk-nav-link nk-nav-expertises-viewall-link"
          onClick={onNavClick}
        >
          {t('nav.expertises.viewAll')}
        </Link>
      </li>
      {EXPERTISE_ITEMS.map((item) => (
        <li key={item.slug} className="nk-nav-expertises-item">
          <Link
            to={`/expertises/${item.slug}`}
            className="nk-nav-link nk-nav-expertises-domain-link"
            onClick={onNavClick}
          >
            {t(item.titleKey)}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NioAccueilListMenu({ items, layout, layoutUpdate, tNav }) {
  if (!items?.length) return null;

  const onNavClick = (e) => handleSubmenuNavClick(e, layout, layoutUpdate);

  return (
    <ul className="nk-nav-sub nk-nav-accueil-list">
      {items.map((item) => (
        <li key={item.url} className="nk-nav-accueil-item">
          <Link
            to={item.url}
            className="nk-nav-link nk-nav-accueil-link"
            onClick={onNavClick}
          >
            {tNav(item.title)}
          </Link>
        </li>
      ))}
    </ul>
  );
}

HeaderMenu.Nav = NioNav;
HeaderMenu.DropMenu = NioDropMenu;
HeaderMenu.ExpertisesListMenu = NioExpertisesListMenu;
HeaderMenu.AccueilListMenu = NioAccueilListMenu;
